import type { Field } from '@equinor/amplify-component-lib';
import {
  createContext,
  useMemo,
  useReducer,
  type FC,
  type ReactNode,
} from 'react';
import { DisplayPressureUnit } from 'src/types/prognosisDetails';
import { ViewPrognosesOptions } from 'src/utils/prognosisDashboard';
import { toKebabCase } from 'src/utils/route';

const localStorageKey = 'client-appstatecontext';

interface AppState {
  field: Field | undefined;
  unit: DisplayPressureUnit;
  draftGroup: string;
  togglePrognosisChartXY: boolean;
}

interface AppContextState {
  field: Field | undefined;
  setField: (val: Field) => void;
  formattedFieldName: string;
  draftGroup: ViewPrognosesOptions;
  setDraftGroup: (val: ViewPrognosesOptions) => void;
  unit: DisplayPressureUnit;
  setUnit: (val: DisplayPressureUnit) => void;
  togglePrognosisChartXY: boolean;
  setTogglePrognosisChartXY: (val: boolean) => void;
}

const getDefaultState = (): AppState => {
  const localStorageData = localStorage.getItem(localStorageKey);

  if (localStorageData) {
    return JSON.parse(localStorageData) as AppState;
  }
  return {
    field: undefined,
    unit: DisplayPressureUnit.BAR,
    draftGroup: ViewPrognosesOptions.SUBMITTED_TO_ME,
    togglePrognosisChartXY: false,
  };
};

const updateLocalStorage = (state: AppState) => {
  localStorage.setItem(localStorageKey, JSON.stringify(state));
};

export const AppContext = createContext<AppContextState | undefined>(undefined);

const appStateReducer = (state: AppState, newState: AppState) => {
  updateLocalStorage(newState);
  return newState;
};

interface AppStateContextProviderProps {
  children: ReactNode;
}

export const AppStateContextProvider: FC<AppStateContextProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(appStateReducer, getDefaultState());

  const formattedFieldName = useMemo(() => {
    if (!state.field?.name) return '';
    return toKebabCase(state.field.name);
  }, [state.field]);

  const setField = (val: Field) => {
    dispatch({
      field: val,
      unit: state.unit,
      draftGroup: state.draftGroup,
      togglePrognosisChartXY: state.togglePrognosisChartXY,
    });
  };

  const setDraftGroup = (val: ViewPrognosesOptions) => {
    dispatch({
      field: state.field,
      unit: state.unit,
      draftGroup: val,
      togglePrognosisChartXY: state.togglePrognosisChartXY,
    });
  };

  const setUnit = (val: DisplayPressureUnit) => {
    dispatch({
      field: state.field,
      unit: val,
      draftGroup: state.draftGroup,
      togglePrognosisChartXY: state.togglePrognosisChartXY,
    });
  };

  const setTogglePrognosisChartXY = (val: boolean) => {
    dispatch({
      field: state.field,
      unit: state.unit,
      draftGroup: state.draftGroup,
      togglePrognosisChartXY: val,
    });
  };

  return (
    <AppContext
      value={{
        field: state.field,
        setField,
        formattedFieldName,
        unit: state.unit,
        setUnit,
        draftGroup: state.draftGroup as ViewPrognosesOptions,
        setDraftGroup,
        togglePrognosisChartXY: state.togglePrognosisChartXY,
        setTogglePrognosisChartXY,
      }}
    >
      {children}
    </AppContext>
  );
};
